import { unwrap } from "@sagot/api-client";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { engageKeys } from "../shared/engage-keys";
import { BOOKINGS_PAGE_SIZE, filterStatus, type BookingFilter } from "./bookings-model";

/** What staff do with a booking: answer a request (confirm / decline) or call off one that is requested or confirmed (cancel). */
export type Decision = "confirm" | "decline" | "cancel";

/** `GET /v1/bookings` with cursor pagination; every page carries the workspace-wide `pending` count. */
export function useBookingList(filter: BookingFilter) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useInfiniteQuery({
    // Under the leads key so confirming a booking (which also writes a lead note) refreshes both.
    queryKey: [...engageKeys.leads(wsId), "bookings", filter] as const,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => unwrap(api.GET("/v1/bookings", { params: { query: { status: filterStatus(filter), cursor: pageParam, limit: BOOKINGS_PAGE_SIZE } } })),
    getNextPageParam: (last) => last.next_cursor,
  });
}

/** `POST /v1/bookings/{id}/confirm|decline|cancel` with an optional note. */
export function useDecideBooking() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, note }: { id: string; decision: Decision; note: string | null }) => {
      const request = { params: { path: { booking_id: id } }, body: { note } };
      if (decision === "confirm") return unwrap(api.POST("/v1/bookings/{booking_id}/confirm", request));
      if (decision === "decline") return unwrap(api.POST("/v1/bookings/{booking_id}/decline", request));
      return unwrap(api.POST("/v1/bookings/{booking_id}/cancel", request));
    },
    onSettled: () => client.invalidateQueries({ queryKey: engageKeys.leads(wsId) }),
  });
}
